// Briefing scene — AI-powered daily planner.
// Server-persisted via useBriefing / useUpsertBriefing etc. LLM call goes
// through /api/llm (callLLM) which handles rate limiting and auth.

import { useState, useEffect, useRef, useMemo } from 'react';
import { Icon } from '../components/Icon.jsx';
import { CheckIcon } from '../components/CheckIcon.jsx';
import { Waveform } from '../components/Waveform.jsx';
import { ProcessingOrb } from '../components/ProcessingOrb.jsx';
import {
  useBriefing, useUpsertBriefing, useUpdateBriefingTask, useDeleteBriefingTask,
  useProfile, useUpdateProfile,
} from '../lib/hooks.js';
import {
  todayIsoKey, yesterdayIsoKey, niceDateDisplay, longDateGreeting, msUntilNextMidnight,
} from '../lib/dateKeys.js';
import { callLLM, transcribeAudio, PaywallRequiredError } from '../lib/llm.js';
import { AIConsentDeclinedError } from '../lib/aiConsent.js';
import { containsSlur, BLOCKED_MESSAGE } from '../lib/safety.js';
import { sfx } from '../lib/sfx.js';
import * as audioStore from '../lib/audioPlayerStore.js';

const LAST_SEEN_DAY_KEY = 'consistency_last_seen_day';

const SYSTEM_PROMPT = () => `You are a daily briefing assistant inside a habit tracking app. The user will describe their day, tasks, meetings, and priorities in natural language (often as a voice transcript, so expect informal speech). Parse their input and return a structured JSON response. Do not include any markdown, backticks, or preamble. Return ONLY valid JSON in this exact format:
{
  "tasks": [
    { "text": "Task description", "priority": "HIGH|MED|LOW|HOLD" }
  ],
  "calls": [
    {
      "title": "Meeting name",
      "time": "10:30 AM ET",
      "priorities": ["Talking point 1", "Talking point 2"]
    }
  ],
  "focus": "One or two sentence summary of deep work priorities for the day",
  "date_display": "${niceDateDisplay()}"
}
If there are no calls, return an empty calls array. If there's no clear focus block, omit the focus field. Infer priority levels from context and urgency cues. Order tasks by priority (HIGH first) and calls chronologically.`;

const ADD_MORE_PROMPT = (existing) =>
  `${SYSTEM_PROMPT()}\n\nThe user already has a briefing for today. Here it is as JSON:\n${JSON.stringify(existing, null, 2)}\n\nThe user is ADDING to this briefing. Return a new JSON that MERGES any new tasks/calls/focus with the existing one. Preserve existing tasks (do not duplicate). Keep their "checked" state if present by not removing or reordering previous tasks — append new ones at the end. If there's new focus content, append it to the existing focus string.`;

const parseModelJson = (raw) => {
  if (!raw) return null;
  let s = raw.trim();
  s = s.replace(/^```(?:json)?\s*/i, '').replace(/```\s*$/, '').trim();
  const first = s.indexOf('{');
  const last = s.lastIndexOf('}');
  if (first !== -1 && last !== -1 && last > first) s = s.slice(first, last + 1);
  try { return JSON.parse(s); } catch { return null; }
};

// Shared text styles
const labelCss = {
  fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 10,
  letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--fg-3)',
};
const monoCss = {
  fontFamily: 'var(--font-display)', fontWeight: 600,
  letterSpacing: '0.12em', textTransform: 'uppercase',
  fontVariantNumeric: 'tabular-nums',
};

// Waveform was lifted into ../components/Waveform.jsx so the Goals voice
// flow can render the same equalizer. Imported above.

// ─── PriorityPill ───────────────────────────────────────────────────
function PriorityPill({ priority }) {
  const p = (priority || '').toUpperCase();
  const cfg = {
    HIGH: { bg: 'rgba(255,77,106,0.16)', fg: '#FF8699', border: 'rgba(255,77,106,0.35)', glow: 'rgba(255,77,106,0.18)' },
    MED:  { bg: 'rgba(255,181,71,0.14)', fg: '#FFC977', border: 'rgba(255,181,71,0.32)', glow: 'rgba(255,181,71,0.14)' },
    LOW:  { bg: 'rgba(45,156,255,0.14)', fg: 'var(--accent-hi)', border: 'rgba(45,156,255,0.3)', glow: 'var(--glow-soft)' },
    HOLD: { bg: 'rgba(255,255,255,0.04)', fg: 'var(--fg-4)', border: 'var(--stroke-2)', glow: 'transparent' },
  }[p];
  if (!cfg) return null;
  const label = p === 'HOLD' ? 'ON HOLD' : p;
  return (
    <span style={{
      ...monoCss, fontSize: 9,
      padding: '3px 8px', borderRadius: 6,
      background: cfg.bg, color: cfg.fg,
      border: `1px solid ${cfg.border}`,
      boxShadow: `0 0 10px ${cfg.glow}`,
      flexShrink: 0,
    }}>{label}</span>
  );
}

// ─── RoundCTA ───────────────────────────────────────────────────────
function RoundCTA({ onClick, children, size = 56 }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: size, height: size, borderRadius: '50%',
        background: 'var(--grad-cta)', border: 'none',
        display: 'grid', placeItems: 'center', cursor: 'pointer',
        boxShadow: '0 0 24px rgba(45,156,255,0.55), inset 0 1px 0 rgba(255,255,255,0.3), 0 8px 28px rgba(0,0,0,0.45)',
        transition: 'transform 0.15s ease, box-shadow 0.2s ease',
        color: '#ffffff',
      }}
      onMouseDown={e => e.currentTarget.style.transform = 'scale(0.94)'}
      onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
      onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
    >
      {children}
    </button>
  );
}

// ─── NamePrompt ─────────────────────────────────────────────────────
function NamePrompt({ onSubmit }) {
  const [name, setName] = useState('');
  const [err, setErr] = useState('');
  const ref = useRef(null);
  useEffect(() => { setTimeout(() => ref.current?.focus({ preventScroll: true }), 120); }, []);
  const submit = () => {
    const n = name.trim();
    if (!n) return;
    if (containsSlur(n)) { setErr(BLOCKED_MESSAGE); return; }
    sfx.toast && sfx.toast(); onSubmit(n);
  };
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', minHeight: 380, gap: 20, padding: 20,
    }}>
      <div style={{
        fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 700,
        letterSpacing: '-0.02em', textAlign: 'center',
      }}>
        <span className="grad-text">Hey</span>, what should I call you?
      </div>
      <input
        ref={ref}
        className="input"
        value={name}
        onChange={e => { setName(e.target.value); setErr(''); sfx.tick && sfx.tick(); }}
        onKeyDown={e => e.key === 'Enter' && submit()}
        placeholder="First name"
        maxLength={40}
        style={{ width: '72%', maxWidth: 300, textAlign: 'center' }}
      />
      {err && <div style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: '#FF8A9E', textAlign: 'center', maxWidth: 300 }}>{err}</div>}
      <button
        onClick={submit}
        disabled={!name.trim()}
        className="btn btn-primary"
        style={{
          padding: '11px 28px', borderRadius: 999,
          opacity: name.trim() ? 1 : 0.45,
          cursor: name.trim() ? 'pointer' : 'not-allowed',
        }}
      >Continue</button>
    </div>
  );
}

// ─── BriefingIntro ──────────────────────────────────────────────────
function BriefingIntro({ name, onReady }) {
  const { dow, month, day, suffix } = longDateGreeting();
  const [stage, setStage] = useState(0);
  useEffect(() => {
    sfx.greeting && sfx.greeting();
    const timers = [
      setTimeout(() => setStage(1), 700),
      setTimeout(() => setStage(2), 1500),
      setTimeout(() => setStage(3), 2400),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  const line3 = `It's ${month} ${day}${suffix}. What's on your plate today?`;
  const lineStyle = (visible) => ({
    fontFamily: 'var(--font-display)',
    fontSize: 26, fontWeight: 700, letterSpacing: '-0.025em',
    color: 'var(--fg-1)', lineHeight: 1.3, textAlign: 'center',
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(8px)',
    transition: 'opacity 0.7s ease, transform 0.7s ease',
    filter: visible ? 'blur(0)' : 'blur(2px)',
  });
  const line3Style = (visible) => ({
    ...lineStyle(visible), fontSize: 15, fontWeight: 500,
    color: 'var(--fg-3)', letterSpacing: '-0.005em',
    marginTop: 16, maxWidth: 320,
  });
  const hasSpeech = typeof window !== 'undefined' &&
    !!(navigator?.mediaDevices?.getUserMedia) && !!window.MediaRecorder;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', minHeight: 380, gap: 8, padding: '10px 24px',
    }}>
      <div style={lineStyle(stage >= 0)}>
        {name ? (<><span className="grad-text">Hello</span> {name}.</>) : (<span className="grad-text">Hello.</span>)}
      </div>
      <div style={lineStyle(stage >= 1)}>Happy {dow}.</div>
      <div style={line3Style(stage >= 2)}>{line3}</div>

      <div style={{
        display: 'flex', gap: 10, marginTop: 30,
        opacity: stage >= 3 ? 1 : 0,
        transform: stage >= 3 ? 'translateY(0)' : 'translateY(10px)',
        transition: 'opacity 0.6s ease, transform 0.6s ease',
      }}>
        <button
          onClick={() => { sfx.nav && sfx.nav(); onReady('text'); }}
          className="btn btn-primary"
          style={{ padding: '11px 22px', borderRadius: 999, gap: 8, display: 'inline-flex', alignItems: 'center' }}
        >
          <Icon name="clipboard" size={14} color="#fff"/> Type
        </button>
        {hasSpeech && (
          <button
            onClick={() => { sfx.nav && sfx.nav(); onReady('voice'); }}
            className="btn"
            style={{
              padding: '11px 22px', borderRadius: 999, gap: 8, display: 'inline-flex', alignItems: 'center',
              background: 'rgba(255,255,255,0.06)', color: 'var(--fg-1)',
              border: '1px solid var(--stroke-2)',
            }}
          >
            <Icon name="mic" size={14}/> Speak
          </button>
        )}
      </div>
    </div>
  );
}

// ─── EmptyState ─────────────────────────────────────────────────────
// ─── CarryoverPrompt ────────────────────────────────────────────────
// Shown on today's empty state when yesterday's agenda exists and has
// unchecked action items. Lets the user pull a selected subset forward
// before dictating today's brief. Gated strictly on the PREVIOUS
// CALENDAR DAY (1 day ago, anchored at local midnight) — if the user
// skipped a day, we do not reach back for items from two days ago.
//
// UX notes:
// - All items default to selected. Users are more likely to keep the
//   whole list than to construct one from scratch, and unchecking a row
//   is cheaper than checking one.
// - Priority is preserved (HIGH stays HIGH). Checked state resets to
//   false — the whole point of carryover is that these are still open.
// - "Skip" persists in localStorage keyed by today's iso date, so the
//   prompt does not reappear if the user tabs away and comes back.
function BriefingCarryoverPrompt({ tasks, onAdd, onSkip }) {
  const [selected, setSelected] = useState(() => new Set(tasks.map(t => t.id)));
  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    sfx.tick && sfx.tick();
    setSelected(next);
  };
  const selectedCount = selected.size;
  const priorityColor = (p) => {
    const k = (p || 'MED').toUpperCase();
    if (k === 'HIGH') return '#FF8A6B';
    if (k === 'LOW') return 'var(--fg-3)';
    if (k === 'HOLD') return 'var(--fg-4)';
    return 'var(--accent-hi)';
  };

  return (
    <div style={{ padding: '12px 20px 4px' }}>
      <div style={{
        background: `
          linear-gradient(180deg, rgba(45,156,255,0.10) 0%, rgba(45,156,255,0.00) 55%),
          var(--glass-bg)
        `,
        backdropFilter: 'var(--glass-blur)',
        WebkitBackdropFilter: 'var(--glass-blur)',
        borderRadius: 16,
        border: '1px solid var(--stroke-1)',
        padding: '14px 16px 12px',
        boxShadow: `
          inset 0 1px 0 rgba(255,255,255,0.08),
          0 0 0 1px rgba(45,156,255,0.10),
          0 8px 24px rgba(0,0,0,0.25)
        `,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <Icon name="clipboard" size={12} color="var(--accent-hi)"/>
          <div style={{
            ...labelCss, fontSize: 9.5, margin: 0, letterSpacing: '0.22em',
            color: 'var(--accent-hi)',
          }}>
            From yesterday
          </div>
        </div>
        <div style={{
          fontFamily: 'var(--font-display)', fontSize: 15.5, fontWeight: 700,
          color: 'var(--fg-1)', letterSpacing: '-0.01em', marginBottom: 3,
        }}>
          {tasks.length === 1
            ? '1 item is still open from yesterday'
            : `${tasks.length} items are still open from yesterday`}
        </div>
        <div style={{
          fontFamily: 'var(--font-body)', fontSize: 12.5, lineHeight: 1.45,
          color: 'var(--fg-3)', marginBottom: 12,
        }}>
          Pull the ones you want to keep working on today.
        </div>

        <div style={{
          display: 'flex', flexDirection: 'column', gap: 4,
          maxHeight: 260, overflowY: 'auto',
          margin: '0 -4px 10px',
        }}>
          {tasks.map((t) => {
            const isOn = selected.has(t.id);
            return (
              <button
                key={t.id}
                onClick={() => toggle(t.id)}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  padding: '8px 10px', borderRadius: 10,
                  background: isOn ? 'rgba(45,156,255,0.08)' : 'transparent',
                  border: '1px solid',
                  borderColor: isOn ? 'rgba(45,156,255,0.25)' : 'var(--stroke-2)',
                  cursor: 'pointer', textAlign: 'left',
                  transition: 'background 0.15s ease, border-color 0.15s ease',
                }}
              >
                <span style={{
                  width: 16, height: 16, borderRadius: 4,
                  border: '1.5px solid',
                  borderColor: isOn ? 'var(--accent-hi)' : 'var(--fg-4)',
                  background: isOn ? 'var(--accent-hi)' : 'transparent',
                  display: 'grid', placeItems: 'center', flexShrink: 0,
                  marginTop: 1,
                  transition: 'all 0.15s ease',
                }}>
                  {isOn && <CheckIcon size={10} color="#fff" stroke={3}/>}
                </span>
                <span style={{
                  fontFamily: 'var(--font-body)', fontSize: 13, lineHeight: 1.4,
                  color: isOn ? 'var(--fg-1)' : 'var(--fg-2)', flex: 1,
                }}>
                  {t.text}
                </span>
                <span style={{
                  ...monoCss, fontSize: 9, letterSpacing: '0.18em',
                  color: priorityColor(t.priority),
                  flexShrink: 0, marginTop: 3,
                }}>
                  {(t.priority || 'MED').toUpperCase()}
                </span>
              </button>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={() => { sfx.nav && sfx.nav(); onSkip(); }}
            className="brief-pill"
            style={{ background: 'transparent' }}
          >
            Skip
          </button>
          <button
            onClick={() => {
              if (selectedCount === 0) { sfx.nav && sfx.nav(); onSkip(); return; }
              sfx.toast && sfx.toast();
              onAdd(tasks.filter(t => selected.has(t.id)));
            }}
            className="brief-pill"
            style={{
              background: 'var(--grad-cta)',
              border: 'none',
              color: '#fff',
              boxShadow: '0 0 12px rgba(45,156,255,0.35), inset 0 1px 0 rgba(255,255,255,0.2)',
            }}
          >
            {selectedCount === 0
              ? 'Skip'
              : selectedCount === tasks.length
                ? `Add all ${selectedCount}`
                : `Add ${selectedCount}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function BriefingEmptyState({ onStart }) {
  const hasSpeech = typeof window !== 'undefined' &&
    !!(navigator?.mediaDevices?.getUserMedia) && !!window.MediaRecorder;
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', minHeight: 380, gap: 20, padding: 20,
    }}>
      <RoundCTA onClick={() => { sfx.nav && sfx.nav(); onStart('text'); }}>
        <Icon name="plus" size={22} color="#fff" stroke={2.6}/>
      </RoundCTA>
      <div style={{
        fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 600,
        color: 'var(--fg-1)', letterSpacing: '-0.015em', marginTop: -4,
      }}>
        Tap to build today's agenda
      </div>
      <div style={{
        fontFamily: 'var(--font-body)', fontSize: 13, lineHeight: 1.5,
        color: 'var(--fg-3)', textAlign: 'center', maxWidth: 300, marginTop: -10,
      }}>
        Talk it out. What's on your plate, who you're meeting, what actually matters today. We'll turn the rant into a crisp plan.
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button onClick={() => { sfx.nav && sfx.nav(); onStart('text'); }} className="brief-pill">
          <Icon name="clipboard" size={12}/> Type
        </button>
        {hasSpeech && (
          <button onClick={() => { sfx.nav && sfx.nav(); onStart('voice'); }} className="brief-pill">
            <Icon name="mic" size={12}/> Voice
          </button>
        )}
      </div>
    </div>
  );
}

// ─── TextInput ──────────────────────────────────────────────────────
function BriefingTextInput({ initialValue, onGenerate, onCancel, onSwitchToVoice, showVoice, appending }) {
  const [text, setText] = useState(initialValue || '');
  const [err, setErr] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    const t = setTimeout(() => ref.current?.focus({ preventScroll: true }), 60);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    if (!err) return;
    if (!containsSlur(text)) setErr('');
  }, [text]); // eslint-disable-line
  const submit = () => {
    if (!text.trim()) return;
    if (containsSlur(text)) { sfx.nav && sfx.nav(); setErr(BLOCKED_MESSAGE); return; }
    sfx.toast && sfx.toast();
    onGenerate(text.trim());
  };
  return (
    <div style={{ padding: '12px 20px 20px' }}>
      <div style={{
        background: 'var(--glass-bg)',
        backdropFilter: 'var(--glass-blur)',
        WebkitBackdropFilter: 'var(--glass-blur)',
        borderRadius: 18,
        border: '1px solid var(--stroke-1)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 12px 40px rgba(0,0,0,0.35)',
        padding: 14, position: 'relative',
      }}>
        <textarea
          ref={ref}
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder={appending ? "Anything to add?" : "What's on your plate today?"}
          style={{
            width: '100%', minHeight: 140, resize: 'none',
            background: 'transparent', border: 'none', outline: 'none',
            color: 'var(--fg-1)', fontFamily: 'var(--font-body)', fontSize: 14.5, lineHeight: 1.55,
          }}
        />
        {showVoice && (
          <button
            onClick={() => { sfx.nav && sfx.nav(); onSwitchToVoice(); }}
            title="Switch to voice"
            style={{
              position: 'absolute', bottom: 10, right: 10,
              width: 34, height: 34, borderRadius: 10,
              background: 'rgba(45,156,255,0.12)',
              border: '1px solid rgba(45,156,255,0.28)',
              display: 'grid', placeItems: 'center',
              color: 'var(--accent-hi)', cursor: 'pointer',
              boxShadow: '0 0 12px rgba(45,156,255,0.2)',
            }}
          >
            <Icon name="mic" size={14}/>
          </button>
        )}
      </div>
      {err && (
        <div style={{
          marginTop: 10,
          fontFamily: 'var(--font-body)', fontSize: 12.5,
          color: '#ff8a8a', lineHeight: 1.45,
        }}>{err}</div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button
          onClick={() => { sfx.sheetClose && sfx.sheetClose(); onCancel(); }}
          className="btn"
          style={{
            padding: '11px 18px', borderRadius: 999,
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid var(--stroke-1)', color: 'var(--fg-3)',
          }}
        >Cancel</button>
        <button
          onClick={submit}
          disabled={!text.trim() || !!err}
          className="btn btn-primary"
          style={{
            flex: 1, padding: '11px 18px', borderRadius: 999,
            opacity: (text.trim() && !err) ? 1 : 0.35,
            cursor: (text.trim() && !err) ? 'pointer' : 'not-allowed',
          }}
        >
          {appending ? 'Add to agenda' : 'Build agenda'}
        </button>
      </div>
    </div>
  );
}

// ─── VoiceInput ─────────────────────────────────────────────────────
// Real voice input: record with MediaRecorder → upload blob to /api/stt
// (OpenAI Whisper). Replaces the prior Web Speech API implementation,
// which silently fails inside iOS WKWebView.
function BriefingVoiceInput({ onFinish, onCancel }) {
  const [phase, setPhase] = useState('init'); // init | recording | transcribing | error
  const [elapsed, setElapsed] = useState(0);
  const [errMsg, setErrMsg] = useState('');
  // Live equalizer analyser — populated once we have a MediaStream. Kept in
  // state (not a ref) so the <Waveform/> child re-renders with the real
  // AnalyserNode and switches from the CSS-pulse fallback to live FFT.
  const [analyser, setAnalyser] = useState(null);

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(0);
  const tickRef = useRef(null);
  const cancelledRef = useRef(false);
  const finishingRef = useRef(false);
  // Web Audio graph for the live equalizer. We tee the stream into an
  // AnalyserNode in parallel with MediaRecorder — the analyser is purely
  // for visuals and never touches the audio that gets sent to Whisper.
  const audioCtxRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const analyserNodeRef = useRef(null);

  // Stop the mic stream and any tick. Idempotent.
  const teardown = () => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    try { recorderRef.current?.state === 'recording' && recorderRef.current.stop(); } catch {}
    try { streamRef.current?.getTracks()?.forEach(t => t.stop()); } catch {}
    recorderRef.current = null;
    streamRef.current = null;
    // Tear down the equalizer audio graph. Disconnect first, then close the
    // context — closing an already-disconnected context is the supported
    // order and avoids "cannot disconnect a node that was not connected".
    try { sourceNodeRef.current?.disconnect(); } catch {}
    try { analyserNodeRef.current?.disconnect(); } catch {}
    try { audioCtxRef.current?.close?.(); } catch {}
    sourceNodeRef.current = null;
    analyserNodeRef.current = null;
    audioCtxRef.current = null;
    setAnalyser(null);
  };

  // Pick the best supported mime type for this browser. iOS Safari/WKWebView
  // prefers audio/mp4; Chrome produces audio/webm. Fall back to default.
  const pickMime = () => {
    const candidates = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/mpeg',
      'audio/wav',
    ];
    if (!window.MediaRecorder?.isTypeSupported) return '';
    for (const t of candidates) {
      try { if (MediaRecorder.isTypeSupported(t)) return t; } catch {}
    }
    return '';
  };

  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        if (!navigator?.mediaDevices?.getUserMedia) {
          throw new Error('Microphone API not available on this device.');
        }
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (aborted) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;

        // ── Live equalizer graph ──────────────────────────────────────
        // Tee the raw microphone stream into an AnalyserNode so <Waveform/>
        // can read real-time FFT data. This is entirely parallel to the
        // MediaRecorder path — MediaRecorder still gets the untouched
        // stream, so Whisper transcription quality is unchanged.
        //
        // Wrapped in try/catch because AudioContext can fail on older
        // WebViews or when the page is suspended; a failure here should
        // never block recording, it just means the bars will fall back
        // to the CSS pulse animation.
        try {
          const AC = window.AudioContext || window.webkitAudioContext;
          if (AC) {
            const ctx = new AC();
            // iOS WKWebView often creates the context in "suspended" state
            // because of autoplay policy. resume() is a no-op if already
            // running; the Promise rejection is harmless either way.
            try { ctx.resume?.().catch(() => {}); } catch {}
            const source = ctx.createMediaStreamSource(stream);
            const node = ctx.createAnalyser();
            node.fftSize = 256;                 // 128 usable bins — plenty for 13 bars
            node.smoothingTimeConstant = 0.75;  // gentle easing, avoids twitchy jitter
            node.minDecibels = -85;
            node.maxDecibels = -20;
            source.connect(node);
            // NB: we do NOT connect the analyser to ctx.destination. That
            // would echo the mic back to the speakers and cause feedback.
            audioCtxRef.current = ctx;
            sourceNodeRef.current = source;
            analyserNodeRef.current = node;
            setAnalyser(node);
          }
        } catch { /* equalizer is decorative; ignore init failures */ }

        const mimeType = pickMime();
        const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
        recorderRef.current = rec;
        chunksRef.current = [];

        rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
        rec.onstop = async () => {
          // Clean up the mic; transcription happens below.
          try { streamRef.current?.getTracks()?.forEach(t => t.stop()); } catch {}
          streamRef.current = null;

          if (cancelledRef.current) return;

          const type = chunksRef.current[0]?.type || rec.mimeType || 'audio/webm';
          const blob = new Blob(chunksRef.current, { type });
          if (!blob.size) {
            setErrMsg("I didn't catch any audio. Try again?");
            setPhase('error');
            return;
          }

          setPhase('transcribing');
          sfx.startTranscribing && sfx.startTranscribing();
          try {
            const text = await transcribeAudio(blob);
            if (cancelledRef.current) return;
            if (!text) {
              setErrMsg("I couldn't make out any speech. Try again?");
              setPhase('error');
              return;
            }
            sfx.ready && sfx.ready();
            onFinish(text);
          } catch (err) {
            if (cancelledRef.current) return;
            // User declined the AI consent prompt — drop back to idle so
            // they can leave the voice flow without an error toast.
            if (err instanceof AIConsentDeclinedError) {
              setPhase('idle');
              return;
            }
            // Free-tier cap hit — the paywall sheet is already on screen
            // via briefApi's 402 handler. Drop quietly back to idle.
            if (err instanceof PaywallRequiredError) {
              setPhase('idle');
              return;
            }
            setErrMsg(err?.message || 'Transcription failed. Try again?');
            setPhase('error');
          } finally {
            sfx.stopTranscribing && sfx.stopTranscribing();
          }
        };

        rec.start();
        startedAtRef.current = Date.now();
        tickRef.current = setInterval(() => {
          setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
        }, 250);
        setPhase('recording');
      } catch (err) {
        if (aborted) return;
        // NotAllowedError → user denied / Info.plist missing on iOS
        const msg = err?.name === 'NotAllowedError'
          ? 'Microphone access was denied. Enable it in Settings to use voice input.'
          : (err?.message || 'Could not access the microphone.');
        setErrMsg(msg);
        setPhase('error');
      }
    })();

    return () => {
      aborted = true;
      cancelledRef.current = true;
      // Belt and suspenders: stop the transcribing ambient if it was running
      // when the user navigated away or cancelled mid-flight.
      try { sfx.stopTranscribing && sfx.stopTranscribing(); } catch {}
      teardown();
    };
  }, []); // eslint-disable-line

  const stopAndTranscribe = () => {
    if (finishingRef.current) return;
    finishingRef.current = true;
    sfx.toast && sfx.toast();
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    // rec.onstop will push through transcription.
    try { recorderRef.current?.stop(); } catch {}
  };

  const cancel = () => {
    cancelledRef.current = true;
    sfx.sheetClose && sfx.sheetClose();
    teardown();
    onCancel();
  };

  const retry = () => {
    setErrMsg('');
    cancelledRef.current = false;
    finishingRef.current = false;
    setElapsed(0);
    setPhase('init');
    // Fire the effect again by remounting: simplest is to onCancel back to
    // idle, but we can just re-run manually. Using a tiny trick — resetting
    // cancelledRef and firing a fresh getUserMedia through the same code.
    // Easier: bubble up "cancel" so parent re-opens voice mode.
    cancel();
  };

  const mm = String(Math.floor(elapsed / 60)).padStart(1, '0');
  const ss = String(elapsed % 60).padStart(2, '0');

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', minHeight: 380, gap: 22, padding: 20,
    }}>
      {phase === 'transcribing' ? (
        // Whisper is running. Swap the live equalizer (which has nothing
        // to sample once the recorder stops) for a proper loading orb +
        // status copy. The transcribing ambient SFX is already running.
        <ProcessingOrb
          size={68}
          label="Transcribing"
          sublabel="Turning your voice into the day's plan."
        />
      ) : (
        <>
          <div style={{ position: 'relative', display: 'grid', placeItems: 'center' }}>
            <div style={{
              position: 'absolute', inset: -40, borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(45,156,255,0.35), transparent 60%)',
              filter: 'blur(18px)',
              animation: 'briefPulse 1.8s ease-in-out infinite',
              opacity: phase === 'recording' ? 1 : 0.35,
            }}/>
            <Waveform analyser={analyser}/>
          </div>

          <div style={{
            fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--fg-3)',
            textAlign: 'center', maxWidth: 280, minHeight: 32, lineHeight: 1.45,
          }}>
            {phase === 'init' && (
              <span style={{ ...monoCss, fontSize: 10, color: 'var(--accent-hi)' }}>
                Waking up the mic…
              </span>
            )}
            {phase === 'recording' && (
              <div>
                <div style={{ ...monoCss, fontSize: 10, color: 'var(--accent-hi)' }}>Listening</div>
                <div style={{ ...monoCss, fontSize: 22, color: 'var(--fg-1)', marginTop: 4 }}>
                  {mm}:{ss}
                </div>
              </div>
            )}
            {phase === 'error' && (
              <span style={{ color: '#FF8699' }}>{errMsg}</span>
            )}
          </div>
        </>
      )}

      {phase === 'recording' && (
        <button onClick={stopAndTranscribe} className="btn btn-primary" style={{ padding: '11px 28px', borderRadius: 999 }}>
          Stop &amp; review
        </button>
      )}
      {phase === 'error' && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={cancel} className="brief-pill">Cancel</button>
          <button onClick={retry} className="btn btn-primary" style={{ padding: '9px 18px', borderRadius: 999 }}>
            Try again
          </button>
        </div>
      )}
      {(phase === 'init' || phase === 'recording' || phase === 'transcribing') && (
        <button
          onClick={cancel}
          style={{
            padding: '8px 16px', borderRadius: 8,
            background: 'transparent', border: 'none',
            color: 'var(--fg-4)', cursor: 'pointer',
            fontFamily: 'var(--font-body)', fontSize: 12,
          }}
        >Cancel</button>
      )}
    </div>
  );
}

// ─── Processing ─────────────────────────────────────────────────────
function BriefingProcessing() {
  // Proper ambient pad while Claude works — the same loop the Intel
  // Brief uses for its generation phase, so the two AI features have a
  // matched audio identity. Replaces the prior 900ms pulse-tick which
  // read as anxious metronome on iOS.
  useEffect(() => {
    sfx.startThinking && sfx.startThinking();
    return () => { sfx.stopThinking && sfx.stopThinking(); };
  }, []);
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', minHeight: 380, padding: 20,
    }}>
      <ProcessingOrb
        size={72}
        label="Building your agenda"
        sublabel="Pulling priorities, calls, and quick wins out of what you said."
      />
    </div>
  );
}

// ─── Error ──────────────────────────────────────────────────────────
function BriefingError({ message, onRetry, onCancel }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', minHeight: 300, gap: 16, padding: 20, textAlign: 'center',
    }}>
      <div style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: '#FF8699' }}>
        {message || "Couldn't parse that. Try again?"}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={onCancel} className="brief-pill">Cancel</button>
        <button onClick={onRetry} className="btn btn-primary" style={{ padding: '9px 18px', borderRadius: 999 }}>Retry</button>
      </div>
    </div>
  );
}

// ─── TaskRow ────────────────────────────────────────────────────────
function TaskRow({ task, onToggle, onEdit, onDelete, onCyclePriority }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(task.text);
  const [menu, setMenu] = useState(false);
  const pressTimer = useRef(null);

  useEffect(() => { setDraft(task.text); }, [task.text]);

  const startPress = () => { pressTimer.current = setTimeout(() => { sfx.nav && sfx.nav(); setMenu(true); }, 450); };
  const cancelPress = () => { if (pressTimer.current) clearTimeout(pressTimer.current); };
  const commit = () => {
    const v = draft.trim();
    if (v && containsSlur(v)) {
      sfx.nav && sfx.nav();
      setDraft(task.text);
      setEditing(false);
      return;
    }
    if (v && v !== task.text) { sfx.toast && sfx.toast(); onEdit(v); }
    else setDraft(task.text);
    setEditing(false);
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 12,
      padding: '12px 0',
      borderBottom: '1px solid var(--stroke-1)',
      position: 'relative',
    }}>
      <div
        onClick={onToggle}
        style={{
          width: 24, height: 24, borderRadius: '50%',
          border: task.checked ? 'none' : '1.8px solid var(--stroke-3)',
          background: task.checked ? 'var(--grad-cta)' : 'transparent',
          display: 'grid', placeItems: 'center',
          flexShrink: 0, marginTop: 1, cursor: 'pointer',
          boxShadow: task.checked ? '0 0 14px rgba(45,156,255,0.55), inset 0 1px 0 rgba(255,255,255,0.3)' : 'none',
          transition: 'all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}>
        {task.checked && <CheckIcon size={13} color="#fff" stroke={3}/>}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        {editing ? (
          <input
            autoFocus
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={e => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') { setDraft(task.text); setEditing(false); }
            }}
            style={{
              width: '100%', background: 'transparent', border: 'none',
              borderBottom: '1px solid var(--accent)',
              color: 'var(--fg-1)', fontFamily: 'var(--font-body)', fontSize: 14,
              padding: '1px 0', outline: 'none',
            }}
          />
        ) : (
          <div
            onClick={() => !task.checked && setEditing(true)}
            onMouseDown={startPress}
            onMouseUp={cancelPress}
            onMouseLeave={cancelPress}
            onTouchStart={startPress}
            onTouchEnd={cancelPress}
            style={{
              fontFamily: 'var(--font-body)', fontSize: 14, lineHeight: 1.4,
              color: task.checked ? 'var(--fg-4)' : 'var(--fg-1)',
              textDecoration: task.checked ? 'line-through' : 'none',
              cursor: 'text', transition: 'all 0.2s ease',
              userSelect: 'none', WebkitUserSelect: 'none',
            }}
          >
            {task.text}
          </div>
        )}
      </div>

      <div onClick={() => { sfx.tick && sfx.tick(); onCyclePriority(); }} style={{ cursor: 'pointer' }} title="Tap to cycle priority">
        <PriorityPill priority={task.priority}/>
      </div>

      {menu && (
        <>
          <div onClick={() => setMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 99 }}/>
          <div style={{
            position: 'absolute', right: 0, top: '100%', zIndex: 100, marginTop: 4,
            background: 'rgba(22,24,28,0.95)',
            backdropFilter: 'blur(30px) saturate(180%)',
            border: '1px solid var(--stroke-2)',
            borderRadius: 12, overflow: 'hidden',
            boxShadow: '0 20px 48px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08)',
            minWidth: 180,
          }}>
            <button onClick={() => { setMenu(false); setEditing(true); }} className="ctx-item">Edit</button>
            <button onClick={() => { setMenu(false); sfx.tick && sfx.tick(); onCyclePriority(); }} className="ctx-item">Change priority</button>
            <button onClick={() => { setMenu(false); onDelete(); }} className="ctx-item" style={{ color: '#FF8699' }}>Delete</button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Main scene ─────────────────────────────────────────────────────
export function Briefing({ onFirstBriefingGenerated }) {
  const [isoDate, setIsoDate] = useState(todayIsoKey());
  const [mode, setMode] = useState('idle');
  const [prefillText, setPrefillText] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [appending, setAppending] = useState(false);

  // Guard so we only fire the "first briefing generated" callback once per
  // session. The App-side handler is already gated on guest status + a
  // localStorage flag, so this is just to avoid repeated calls on re-runs.
  const firedFirstBriefingRef = useRef(false);

  const { data: briefing } = useBriefing(isoDate);
  // Pre-fetch yesterday's briefing so we can offer a carryover prompt for
  // unchecked action items. This uses the app's midnight-anchored yesterday, so
  // the query key moves with the rest of the scene at the daily rollover.
  const prevIsoDate = useMemo(() => yesterdayIsoKey(), [isoDate]);
  const { data: yesterdayBriefing } = useBriefing(prevIsoDate);
  const { data: profile } = useProfile();
  const upsertBriefing = useUpsertBriefing();
  const updateTask = useUpdateBriefingTask();
  const deleteTask = useDeleteBriefingTask();
  const updateProfile = useUpdateProfile();

  const userName = profile?.displayName || '';
  const hasSpeech = typeof window !== 'undefined' &&
    !!(navigator?.mediaDevices?.getUserMedia) && !!window.MediaRecorder;

  // Has the current day actually been populated? Tasks OR focus OR calls counts.
  const populated = !!briefing && (
    (briefing.tasks && briefing.tasks.length > 0) ||
    !!briefing.focus ||
    (briefing.calls && briefing.calls.length > 0)
  );

  // Carryover: unchecked items from yesterday's agenda, gated strictly to
  // the previous calendar day. If the user skipped a day, yesterdayBriefing
  // will be empty and this list collapses to zero, so the prompt stays hidden.
  const yesterdayUnmarked = useMemo(() => {
    const list = Array.isArray(yesterdayBriefing?.tasks) ? yesterdayBriefing.tasks : [];
    return list.filter(t => !t.checked);
  }, [yesterdayBriefing]);

  // Dismissal is per-day so tabbing away and coming back does not re-prompt,
  // but a brand new tomorrow starts clean.
  const dismissKey = `proxera.carryoverDismissed.v1:${isoDate}`;
  const [carryoverDismissed, setCarryoverDismissed] = useState(() => {
    try { return localStorage.getItem(dismissKey) === '1'; } catch { return false; }
  });
  useEffect(() => {
    // Rollover re-evaluates dismissal under the new day's key.
    try { setCarryoverDismissed(localStorage.getItem(`proxera.carryoverDismissed.v1:${isoDate}`) === '1'); } catch { setCarryoverDismissed(false); }
  }, [isoDate]);
  const dismissCarryover = () => {
    try { localStorage.setItem(dismissKey, '1'); } catch {}
    setCarryoverDismissed(true);
  };

  // Determine the intro state on first render: if no briefing and we haven't
  // shown the intro for this day yet, prompt name (if missing) or intro.
  useEffect(() => {
    if (populated) {
      setMode('idle');
      try { localStorage.setItem(LAST_SEEN_DAY_KEY, isoDate); } catch {}
      return;
    }
    let lastSeen = '';
    try { lastSeen = localStorage.getItem(LAST_SEEN_DAY_KEY) || ''; } catch {}
    const needsIntro = lastSeen !== isoDate;
    if (needsIntro) setMode(!userName ? 'name' : 'intro');
    else setMode('idle');
  }, [populated, isoDate, userName]);

  // Audio ducking — if an Intelligence brief is playing when the user
  // opens the briefing recorder, pause it for the duration and resume
  // when we return to idle (success) or error. Text-only entry doesn't
  // need ducking but we keep the duck through 'text'→'processing' in
  // case the user got there via voice, so the flow doesn't flap.
  const duckActiveRef = useRef(false);
  useEffect(() => {
    // Duck as soon as the mic is opened. We don't duck on 'text' entry
    // because that flow doesn't use the mic.
    if (mode === 'voice' && !duckActiveRef.current) {
      duckActiveRef.current = true;
      try { audioStore.duck(); } catch {}
    }
    // Unduck once the brief has been generated (idle) or errored. If we
    // never ducked, unduck() is a no-op so this stays cheap.
    if ((mode === 'idle' || mode === 'error') && duckActiveRef.current) {
      duckActiveRef.current = false;
      try { audioStore.unduck(); } catch {}
    }
  }, [mode]);
  // Safety net: if the user leaves the Briefing tab mid-flow, don't
  // strand the brief audio in a paused-by-us state forever.
  useEffect(() => {
    return () => {
      if (duckActiveRef.current) {
        duckActiveRef.current = false;
        try { audioStore.unduck(); } catch {}
      }
    };
  }, []);

  // Midnight rollover — when the day changes, flip to the new key. The
  // query hook will refetch, the intro gate above will re-run.
  useEffect(() => {
    const wait = msUntilNextMidnight();
    const t = setTimeout(() => {
      setIsoDate(todayIsoKey());
      setAppending(false);
      setPrefillText('');
    }, wait + 2000);
    return () => clearTimeout(t);
  }, [isoDate]);

  const markDaySeen = () => {
    try { localStorage.setItem(LAST_SEEN_DAY_KEY, isoDate); } catch {}
  };

  const handleNameSubmit = (n) => {
    updateProfile.mutate({ displayName: n });
    setMode('intro');
  };

  const toggleBriefingTask = (task) => {
    const willBeChecked = !task.checked;
    sfx[willBeChecked ? 'check' : 'uncheck'] && sfx[willBeChecked ? 'check' : 'uncheck']();
    updateTask.mutate({ id: task.id, patch: { checked: willBeChecked }, isoDate });
    // Milestone if all done after this toggle.
    if (willBeChecked && briefing?.tasks?.every(t => t.id === task.id ? true : t.checked)) {
      setTimeout(() => sfx.milestone && sfx.milestone(), 240);
    }
  };
  const editBriefingTask = (task, newText) => {
    updateTask.mutate({ id: task.id, patch: { text: newText }, isoDate });
  };
  const deleteBriefingTask = (task) => {
    sfx.del && sfx.del();
    deleteTask.mutate({ id: task.id, isoDate });
  };
  const cyclePriority = (task) => {
    const order = ['HIGH', 'MED', 'LOW', 'HOLD'];
    const cur = order.indexOf((task.priority || 'MED').toUpperCase());
    const next = order[(cur + 1) % order.length];
    updateTask.mutate({ id: task.id, patch: { priority: next }, isoDate });
  };

  const generate = async (userInput) => {
    setMode('processing');
    try {
      const system = appending && briefing ? ADD_MORE_PROMPT(briefing) : SYSTEM_PROMPT();
      const raw = await callLLM({ system, messages: [{ role: 'user', content: userInput }] });
      const parsed = parseModelJson(raw);
      if (!parsed || (!Array.isArray(parsed.tasks) && !Array.isArray(parsed.calls))) {
        setErrorMsg("Couldn't parse that. Try again?");
        setPrefillText(userInput);
        setMode('error');
        return;
      }
      const newTasks = (parsed.tasks || []).map(t => ({
        text: t.text || '',
        priority: (t.priority || 'MED').toUpperCase(),
        checked: false,
      }));
      let finalTasks = newTasks;
      if (appending && briefing?.tasks) {
        const existingTexts = new Set(briefing.tasks.map(t => t.text.trim().toLowerCase()));
        const fresh = newTasks.filter(t => !existingTexts.has(t.text.trim().toLowerCase()));
        finalTasks = [...briefing.tasks, ...fresh];
      }
      const nextBriefing = {
        tasks: finalTasks,
        calls: parsed.calls || (appending ? (briefing?.calls || []) : []),
        focus: parsed.focus || (appending ? briefing?.focus : null),
        date_display: parsed.date_display || niceDateDisplay(),
      };
      await upsertBriefing.mutateAsync({ isoDate, data: nextBriefing });
      setAppending(false); setPrefillText('');
      setMode('idle'); markDaySeen();
      sfx.briefingReady && sfx.briefingReady();
      // Notify parent so it can offer a soft "save your progress" sheet
      // to guests on their first briefing. App-side gate filters non-guests.
      if (!appending && !firedFirstBriefingRef.current && typeof onFirstBriefingGenerated === 'function') {
        firedFirstBriefingRef.current = true;
        setTimeout(() => onFirstBriefingGenerated(), 700);
      }
    } catch (e) {
      // User declined the AI consent prompt — silently revert to compose
      // mode so they can step out of the flow without seeing an error.
      if (e instanceof AIConsentDeclinedError) {
        setPrefillText(userInput);
        setMode('text');
        return;
      }
      // Free-tier cap on /api/llm — paywall is already on screen.
      if (e instanceof PaywallRequiredError) {
        setPrefillText(userInput);
        setMode('text');
        return;
      }
      setErrorMsg(e?.message || 'Request failed. Check your connection and retry.');
      setPrefillText(userInput);
      setMode('error');
    }
  };

  const startInput = (kind) => {
    setPrefillText('');
    setMode(kind === 'voice' && hasSpeech ? 'voice' : 'text');
  };

  // Accept a subset of yesterday's unchecked items onto today's empty agenda.
  // Priorities are preserved. Checked state resets to false (these are still
  // open by definition). Today's date_display is taken from the current day.
  // After the upsert, the populated view takes over and the user can keep
  // adding via the regular voice/text flow.
  const acceptCarryover = async (selectedTasks) => {
    if (!selectedTasks || selectedTasks.length === 0) {
      dismissCarryover();
      return;
    }
    try {
      const tasks = selectedTasks.map(t => ({
        text: t.text,
        priority: (t.priority || 'MED').toUpperCase(),
        checked: false,
      }));
      await upsertBriefing.mutateAsync({
        isoDate,
        data: {
          tasks,
          calls: [],
          focus: null,
          date_display: niceDateDisplay(),
        },
      });
      dismissCarryover();
      sfx.briefingReady && sfx.briefingReady();
      markDaySeen();
    } catch (e) {
      setErrorMsg(e?.message || 'Could not carry over yesterday\'s items. Try again.');
      setMode('error');
    }
  };

  const taskCount = briefing?.tasks?.length || 0;
  const doneCount = briefing?.tasks?.filter(t => t.checked).length || 0;
  const pct = taskCount > 0 ? Math.round((doneCount / taskCount) * 100) : 0;
  const allDone = taskCount > 0 && doneCount === taskCount;

  const orderedTasks = useMemo(() => {
    if (!briefing?.tasks) return [];
    // Preserve stored order (server sorts by sort_order); no-op here.
    return briefing.tasks;
  }, [briefing?.tasks]);

  return (
    <div className="fade-in" style={{ paddingTop: 4 }}>
      {/* Header */}
      <div style={{ padding: '8px 20px 6px' }}>
        <div style={{ ...labelCss, fontSize: 10, marginBottom: 6 }}>Your Agenda</div>
        <h1 style={{
          fontFamily: 'var(--font-display)',
          fontSize: 36, fontWeight: 800, letterSpacing: '-0.03em',
          margin: 0, lineHeight: 1,
        }}>
          <span className="grad-text">Today</span>
          {populated && briefing?.date_display && (
            <span style={{ color: 'var(--fg-4)', fontWeight: 500, fontSize: 18, marginLeft: 10, letterSpacing: '-0.01em' }}>
              {briefing.date_display}
            </span>
          )}
        </h1>
      </div>

      {/* Progress bar */}
      {populated && taskCount > 0 && (
        <div style={{ padding: '14px 20px 8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 7 }}>
            <span style={{ ...monoCss, fontSize: 10, color: 'var(--fg-3)' }}>
              {doneCount} / {taskCount} complete
            </span>
            <span style={{ ...monoCss, fontSize: 11, color: allDone ? 'var(--accent-hi)' : 'var(--fg-3)' }}>
              {pct}%
            </span>
          </div>
          <div style={{ height: 3, background: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${pct}%`,
              background: 'var(--grad-cta)',
              boxShadow: '0 0 8px rgba(45,156,255,0.6)',
              transition: 'width 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}/>
          </div>
        </div>
      )}

      {/* Modes */}
      {mode === 'name' && <NamePrompt onSubmit={handleNameSubmit}/>}
      {mode === 'intro' && <BriefingIntro name={userName} onReady={(kind) => { markDaySeen(); startInput(kind); }}/>}
      {mode === 'idle' && !populated && yesterdayUnmarked.length > 0 && !carryoverDismissed && (
        <BriefingCarryoverPrompt
          tasks={yesterdayUnmarked}
          onAdd={acceptCarryover}
          onSkip={dismissCarryover}
        />
      )}
      {mode === 'idle' && !populated && <BriefingEmptyState onStart={startInput}/>}

      {mode === 'text' && (
        <BriefingTextInput
          initialValue={prefillText}
          showVoice={hasSpeech}
          appending={appending}
          onGenerate={generate}
          onCancel={() => { setMode('idle'); setAppending(false); }}
          onSwitchToVoice={() => setMode('voice')}
        />
      )}

      {mode === 'voice' && (
        <BriefingVoiceInput
          onFinish={(t) => { setPrefillText(t); setMode('text'); }}
          onCancel={() => setMode('idle')}
        />
      )}

      {mode === 'processing' && <BriefingProcessing/>}

      {mode === 'error' && (
        <BriefingError
          message={errorMsg}
          onRetry={() => setMode('text')}
          onCancel={() => { setMode('idle'); setAppending(false); }}
        />
      )}

      {/* Rendered briefing */}
      {populated && mode === 'idle' && (
        <div style={{ padding: '16px 20px 24px' }}>
          {briefing.focus && (
            // Focus card — marked as a "header" without the left-edge
            // accent stripe. Three cues stacked:
            //  1. A card-wide accent halo glow (box-shadow) so the card
            //     feels lifted off the background.
            //  2. A subtle top-to-bottom accent gradient wash on top of
            //     the glass background — a tint, not a fill.
            //  3. A target icon + accent-colored "FOCUS" label with
            //     tracking, reading as a semantic badge.
            <div style={{
              background: `
                linear-gradient(180deg, rgba(45,156,255,0.10) 0%, rgba(45,156,255,0.00) 65%),
                var(--glass-bg)
              `,
              backdropFilter: 'var(--glass-blur)',
              WebkitBackdropFilter: 'var(--glass-blur)',
              borderRadius: 16,
              border: '1px solid var(--stroke-1)',
              padding: '14px 16px', marginBottom: 20,
              boxShadow: `
                inset 0 1px 0 rgba(255,255,255,0.08),
                0 0 0 1px rgba(45,156,255,0.12),
                0 10px 30px rgba(45,156,255,0.12),
                0 0 48px rgba(45,156,255,0.10)
              `,
              position: 'relative',
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10,
              }}>
                <Icon name="target" size={11} color="var(--accent-hi)"/>
                <div style={{
                  ...labelCss, fontSize: 9.5, margin: 0, letterSpacing: '0.22em',
                  color: 'var(--accent-hi)',
                }}>
                  Focus
                </div>
              </div>
              <div style={{ fontFamily: 'var(--font-body)', fontSize: 14, lineHeight: 1.5, color: 'var(--fg-1)' }}>
                {briefing.focus}
              </div>
            </div>
          )}

          {orderedTasks.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                paddingBottom: 8, marginBottom: 2,
                borderBottom: '1px solid var(--stroke-2)',
              }}>
                <div style={{ ...labelCss, fontSize: 10 }}>Action Items</div>
                <div style={{ ...monoCss, fontSize: 9, color: 'var(--fg-4)' }}>
                  Tap text to edit · long-press for options
                </div>
              </div>
              {orderedTasks.map((t) => (
                <TaskRow
                  key={t.id}
                  task={t}
                  onToggle={() => toggleBriefingTask(t)}
                  onEdit={(newText) => editBriefingTask(t, newText)}
                  onDelete={() => deleteBriefingTask(t)}
                  onCyclePriority={() => cyclePriority(t)}
                />
              ))}
            </div>
          )}

          {briefing.calls && briefing.calls.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{
                ...labelCss, fontSize: 10, marginBottom: 12,
                paddingBottom: 8, borderBottom: '1px solid var(--stroke-2)',
              }}>Today's Calls</div>
              {briefing.calls.map((c, i) => (
                <div key={i} style={{
                  background: 'var(--glass-bg)',
                  backdropFilter: 'var(--glass-blur)',
                  WebkitBackdropFilter: 'var(--glass-blur)',
                  borderRadius: 14,
                  border: '1px solid var(--stroke-1)',
                  padding: 14, marginBottom: 10,
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)',
                }}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'flex-start', gap: 10,
                    marginBottom: c.priorities?.length ? 10 : 0,
                  }}>
                    <div style={{
                      fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700,
                      color: 'var(--fg-1)', flex: 1, lineHeight: 1.3, letterSpacing: '-0.01em',
                    }}>{c.title}</div>
                    {c.time && (
                      <span style={{
                        ...monoCss, fontSize: 10, color: 'var(--fg-1)',
                        background: 'var(--grad-cta)',
                        padding: '4px 9px', borderRadius: 6,
                        boxShadow: '0 0 12px rgba(45,156,255,0.4), inset 0 1px 0 rgba(255,255,255,0.2)',
                        whiteSpace: 'nowrap', flexShrink: 0,
                      }}>{c.time}</span>
                    )}
                  </div>
                  {c.priorities && c.priorities.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {c.priorities.map((p, j) => (
                        <div key={j} style={{
                          display: 'flex', gap: 10, alignItems: 'flex-start',
                          fontFamily: 'var(--font-body)', fontSize: 13, lineHeight: 1.45,
                          color: 'var(--fg-2)',
                        }}>
                          <span style={{ color: 'var(--accent-hi)', flexShrink: 0, fontWeight: 700 }}>→</span>
                          <span>{p}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
            <button
              onClick={() => { sfx.sheetOpen && sfx.sheetOpen(); setAppending(true); setMode('text'); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                background: 'rgba(255,255,255,0.03)',
                border: '1px dashed var(--stroke-2)',
                borderRadius: 999, padding: '9px 18px 9px 12px',
                color: 'var(--fg-3)', cursor: 'pointer',
                fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 12.5,
                letterSpacing: '-0.005em', transition: 'all 0.2s ease',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(45,156,255,0.4)'; e.currentTarget.style.color = 'var(--accent-hi)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--stroke-2)'; e.currentTarget.style.color = 'var(--fg-3)'; }}
            >
              <span style={{
                width: 22, height: 22, borderRadius: '50%',
                background: 'var(--grad-cta)',
                display: 'grid', placeItems: 'center',
                boxShadow: '0 0 10px rgba(45,156,255,0.4)',
              }}>
                <Icon name="plus" size={11} color="#fff" stroke={2.6}/>
              </span>
              Add / Adjust
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
